/*** IMPORTS FROM imports-loader ***/
var THREE = require("three");

import Utils from 'Utils/Utils';
import Logger from 'Utils/Logger';
import EventsEmitter from './../Utils/EventsEmitter';
import InfopointWidget from './InfopointWidget';
import GalleryWidget from './GalleryWidget';
import BannerWidget from './BannerWidget';
import PictureWidget from './PictureWidget';
import VideoWidget from './VideoWidget';
import TransitionWidget from './TransitionWidget';
import NadirWidget from './NadirWidget';
import _minBy from 'lodash/minBy';

export default class WidgetsManager extends EventsEmitter {
  constructor({stage, scene, project, editor, speaker, animations, linksEnabled, audioEnabled}) {
    super();
    this._widgets = {};
    this._scene = scene;
    this._stage = stage;
    this._project = project;
    this.$l = new Logger('WidgetsManager');
    this.$ = new THREE.Group();
    this.$.name = 'WidgetsManager';
    this._scene.add(this.$);
    this._initialized = false; // if true, runs init on widgets with init method
    this._editor = editor;
    this._speaker = speaker;
    this._renderOrderInc = 0; // add incremental number to render order
    this._getRenderOrderInc = this._getRenderOrderInc.bind(this);
    this.animations = animations;
    this.linksEnabled = linksEnabled;
    this.audioEnabled = audioEnabled;
  }
  _getRenderOrderInc() {
    return this._renderOrderInc++;
  }
  createWidget(widgetData) {
    const { id } = widgetData;
    const isIdInvalid = !id;
    const widgetAlreadyExists = !!this._widgets[id];

    if (isIdInvalid) {
      this.$l.error(`Provided invalid widget id: ${id}`);
    }

    if (widgetAlreadyExists) {
      this.$l.error(`Widget with given id: ${id} already exists`);
    }

    try {
      const widgetType = widgetData.type;
      const availableWidgets = {
        'infopoint': InfopointWidget,
        'gallery': GalleryWidget,
        'banner': BannerWidget,
        'picture': PictureWidget,
        'video': VideoWidget,
        'nadir': NadirWidget,
        'transition': TransitionWidget,
        'project-link': TransitionWidget
      };

      const ChosenWidget = availableWidgets[widgetType];
      const isChosenWidgetAvailable = !!ChosenWidget;

      if (!isChosenWidgetAvailable) {
        throw new Error('Unknown widget type: ' + widgetType);
      }

      const newWidget = new ChosenWidget({
        data: widgetData,
        project: this._project,
        editor: this._editor,
        speaker: this._speaker,
        getRenderOrderInc: this._getRenderOrderInc,
        animations: this.animations,
        linksEnabled: this.linksEnabled,
        audioEnabled: this.audioEnabled,
      });

      this._widgets[id] = newWidget;

      newWidget.getState().isReady.then(() => {
        const createdWidget = this._widgets[id];

        createdWidget.on('open', e => this.emit('open', e));
        createdWidget.on('close', e => this.emit('close', e));
        createdWidget.on('action', e => this.emit('action', e));

        this.$.add(createdWidget.$);
      }, (err) => {
        this.$l.error(`Widget: ${id} failed to initialize`);
        delete this._widgets[id];
      });

      this._initialized = false;

      return newWidget;
    } catch(e) {
      this.$l.warn(e);
    }
  }
  reloadWidgets({widgets}) {
    let currentWidgets = Utils.extend({arr: widgets}).arr;

    for (let widgetId in this._widgets) {
      this.removeWidget(widgetId);
    }

    if (!currentWidgets) { return; }

    currentWidgets.forEach(widget => this.createWidget(widget));
  }
  animate(options) {
    if (!this._initialized) {
      this.init();
    } else {
      for (let widgetId in this._widgets) {
        let widget = this._widgets[widgetId];
        if (!widget) { break; }

        if (options.raycaster) {
          if (widget.intersectObject({raycaster: options.raycaster})) {

            if (!widget.getState().opened) {
              widget.open();
            }
          } else {
            if (widget.getState().opened) {
              widget.close();
            }
          }
        }

        if (!this._editor) {
          widget.animateIcon(options);
        }
        widget.animate(options);
      }
    }
  }
  init() {
    if (!this._initialized) {
      let promises = [];
      for (let widgetId in this._widgets) {
        if (this._widgets[widgetId].init) {
          promises.push(this._widgets[widgetId].init());
        }
      }
      Promise.all(promises).then(() => {
        this._initialized = true;
      }, (e) => {
        this.$l.error(e);
      });
    }
  }
  closeWidget(options) {
    let closedIds = [];

    if (options && options.widgetId) {
      if (this._widgets[options.widgetId]) {
        this._widgets[options.widgetId].close({animate: !!options.animating});
        if (this._widgets[options.widgetId].updateHighlightMesh) {
          this._widgets[options.widgetId].updateHighlightMesh();
        }
        closedIds.push(options.widgetId);
      } else {
        this.$l.warn('There is no widget with given id on this scene');
      }
    } else {
      for (let widgetId in this._widgets) {
        let widget = this._widgets[widgetId];
        if (widget._state.opening || widget._state.opened) {
          this._widgets[widgetId].close(!!(options && options.animating));
          if (this._widgets[widgetId].updateHighlightMesh) {
            this._widgets[widgetId].updateHighlightMesh();
          }
          closedIds.push(widgetId);
        }
      }
    }

    return closedIds;
  }
  openWidget(options) {
    let widget = this._widgets[options.widgetId];

    if (widget) {
      if (!this._editor || widget._data.type !== 'transition') {
        widget.open(!!options.animating, options.context);
        if (widget.updateHighlightMesh) {
          widget.updateHighlightMesh();
        }
      }
    } else {
      this.$l.warn('There is no widget with id: ' + options.widgetId);
    }
  }
  toggleNearestWidget(cameraForward) {
    // distance < 0.6 is magic number for angle of ~35 degrees
    let widgets = Object.entries(this._widgets).map(([id, widget]) => {
      const worldDirection = new THREE.Vector3();
      let widgetDirection = widget.$.getWorldDirection().clone().negate();
      return {id: id, widget: widget, distance: widgetDirection.angleTo(cameraForward)};
    }).filter(({distance, widget}) => distance < 0.6 && (widget._data.type === 'infopoint' || widget._data.type === 'gallery' || widget._data.type === 'transition' || widget._data.type === 'project-link'));

    let closestWidget,
      wasOpened = false;

    if (widgets.length) {
      closestWidget = _minBy(widgets, pair => pair.distance);
      wasOpened = closestWidget.widget._state.opening || closestWidget.widget._state.opened;
    }

    // close all other widgets
    this.closeWidget({animating: true});

    if (closestWidget) {
      if (!wasOpened) {
        this.openWidget({widgetId: closestWidget.id, animating: true});
      }
      this.emit('toggleNearestWidget', { target: { id: closestWidget.id }, effective: true });
    } else {
      this.emit('toggleNearestWidget', { effective: false });
    }
  }
  handleClick({ raycaster }) {
    const intersections = [];
    const openedWidgets = [];

    Object.keys(this._widgets).forEach((widgetId) => {
      const widget = this._widgets[widgetId];
      const intersection = widget.intersectObject({ raycaster });

      if (widget._state.opening || widget._state.opened) {
        openedWidgets.push(widgetId);
      }
      if (intersection.intersects) {
        intersections.push(Object.assign({ widget }, intersection));
      }
    });

    intersections.sort((i1, i2) => (i2.widget.renderOrder || 0) - (i1.widget.renderOrder || 0));
    const intersection = intersections[0];
    let closeOpenedWidgets = false;
    let effective = false;

    if (intersection) {
      const { widget, action } = intersection;
      effective = intersection.effective;
      if (action) {
        this.emit('action', action);
        if (action.type === 'openWidget') {
          closeOpenedWidgets = true;
          const widgetToOpen = openedWidgets.find(id => id === action.widgetId);
          const index = openedWidgets.indexOf(widgetToOpen);
          if (index !== -1) {
            openedWidgets.splice(index, 1);
          }
        }
        effective = true;
      } else if (!(widget._state.opening || widget._state.opened)) {
        this.emit('action', {
          type: 'openWidget',
          widgetId: widget._data.id,
          animate: true,
        });
        closeOpenedWidgets = true;
        effective = true;
      }
    } else {
      closeOpenedWidgets = true;
    }

    if (closeOpenedWidgets) {
      openedWidgets.forEach(widgetId => this.closeWidget({ widgetId, animating: true }));
    }

    return {
      targets: intersections.map(i => i.widget),
      effective: intersection
        ? effective
        : !!openedWidgets.length,
    };
  }
  handleMove(mousePosition) {
    for (let widgetId in this._widgets) {
      if (!this._widgets[widgetId].getState().opened) {
        this._widgets[widgetId].handleMove(mousePosition);
      }
    }
  }
  intersectWidgets({raycaster}) {
    let widgets = [];

    for (let widgetId in this._widgets) {
      if (this._widgets[widgetId].intersectObject({raycaster: raycaster}).intersects) {
        widgets.push(this._widgets[widgetId]);
      }
    }

    widgets.sort(function(w1, w2){
      return (w2.renderOrder || 0) - (w1.renderOrder || 0);
    });

    return widgets;
  }
  highlightWidgets(widgetIds) {
    for (let widgetId in this._widgets) {
      this._widgets[widgetId].setHighlight(widgetIds.indexOf(widgetId) > -1);
    }
  }
  removeWidget(widgetId) {
    if (widgetId && this._widgets[widgetId]) {
      this.$.remove(this._widgets[widgetId].$);
      this._widgets[widgetId].remove();
      delete this._widgets[widgetId];
    }
  }
  addWidget(widgetData) {
    this.createWidget(widgetData);
  }
  getWidget(widgetId) {
    return this._widgets[widgetId];
  }
  updateWidget(widgetData) {
    let widget = this._widgets[widgetData.id];

    return new Promise((resolve, reject) => {
      if (widget) {
        let diff = Utils.diff(widget._data, widgetData, 1),
          tiltChanged = false,
          typeChanged = false,
          rotationChanged = false,
          correctionChanged = false,
          otherChanged = false;

        if (diff.indexOf('details.tilt') > -1) {
          tiltChanged = true;
          diff.splice(diff.indexOf('details.tilt'), 1);
        }

        if (diff.indexOf('type') > -1) {
          typeChanged = true;
          diff.splice(diff.indexOf('type'), 1);
        }

        if (diff.indexOf('rotation.y') > -1 || diff.indexOf('rotation.x') > -1) {
          rotationChanged = true;
          diff.indexOf('rotation.y') > -1 && diff.splice(diff.indexOf('rotation.y'), 1);
          diff.indexOf('rotation.x') > -1 && diff.splice(diff.indexOf('rotation.x'), 1);
        }

        if (diff.indexOf('correction.brightness') > -1 || diff.indexOf('correction.contrast') > -1 || diff.indexOf('correction.gamma') > -1 || diff.indexOf('correction.saturation') > -1) {
          correctionChanged = true;
          diff.indexOf('correction.brightness') > -1 && diff.splice(diff.indexOf('correction.brightness'), 1);
          diff.indexOf('correction.contrast') > -1 && diff.splice(diff.indexOf('correction.contrast'), 1);
          diff.indexOf('correction.gamma') > -1 && diff.splice(diff.indexOf('correction.gamma'), 1);
          diff.indexOf('correction.saturation') > -1 && diff.splice(diff.indexOf('correction.saturation'), 1);
        }

        if (diff.length > 0) otherChanged = true;

        if (typeChanged) {
          let wasHighlighted = widget.isHighlighted(),
            wasOpened = widget._state.opened;

          this.removeWidget(widgetData.id);
          let newWidget = this.createWidget(widgetData);

          newWidget.getState().isReady.then(() => {
            newWidget.setHighlight(wasHighlighted);
            if (wasOpened) newWidget.open(false);
            resolve();
          }, reject);
        } else if (correctionChanged) {
          widget.createContent();
          resolve();
        } else if (otherChanged) {
          widget._data = widgetData;
          widget.update(diff)
            .then(resolve, reject);
        } else if (tiltChanged || rotationChanged) {
          if (rotationChanged) {
            widget._data.rotation = widgetData.rotation;
            widget.updateRotationPosition();
          }
          if (tiltChanged) {
            widget._data.details.tilt = widgetData.details.tilt;
            widget.updateTilt();
          }
          resolve();
        } else {
          resolve();
        }
      }});
  }
  removeLinks() {
    Object.entries(this._widgets).map(([id, widget]) => {
      if (widget._data.details.link) {
        if (widget.removeLink) {
          widget.removeLink();
        }
      }
    });
  }
  openCurrentLink() {
    const openWidget = Object.values(this._widgets).find(
      widget => (widget._state.opened || widget._state.opening)
    );

    if (openWidget && openWidget.onLinkOpen) {
      openWidget.onLinkOpen();
    }
  }
  closeCurrentLink() {
    const closeWidget = Object.values(this._widgets).find(
      widget => (widget._state.opened || widget._state.opening)
    );

    if (closeWidget && closeWidget.onLinkClose) {
      closeWidget.onLinkClose();
    }
  }
}

